/* ============================================================
   SOCIAL IRONY — product-gallery.js

   Thumbnail switching for the product page gallery.

   Why this file had to exist:
   The gallery rendered a main image plus three thumbnail buttons carrying
   `data-si-media-select`, but nothing listened for a click. The buttons were
   real <button> elements, so they looked interactive and did nothing. Reporting
   this as "images 2 and 3 cannot be opened" is exactly right.

   What it does:

     - clicking a thumbnail swaps the main image and marks the thumbnail active
     - arrow keys and Home/End move between thumbnails, because a row of buttons
       is a keyboard interface whether or not it was designed as one
     - the main image keeps its `srcset`, so the browser picks the right
       resolution for the current device. Writing `src` alone would downgrade a
       Retina display to the smallest candidate - the same class of bug as the
       blur that was reported earlier.
     - variant changes drive the gallery too, so selecting a colour shows that
       colour's photo.

   No dependencies, no build step.
   ============================================================ */

(function () {
  'use strict';

  const qs = window.SI && window.SI.qs ? window.SI.qs : (sel, root) => (root || document).querySelector(sel);
  const qsa = window.SI && window.SI.qsa
    ? window.SI.qsa
    : (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const gallery = qs('[data-si-gallery]');
  if (!gallery) return;

  const mainFrame = qs('[data-si-main-media]', gallery);
  if (!mainFrame) return;

  const mainImage = qs('img', mainFrame);
  const thumbs = qsa('[data-si-media-select]', gallery);

  if (!mainImage || thumbs.length === 0) return;

  /**
   * Swap the main image to a different media item.
   *
   * The full-size source data comes from data attributes on the thumbnail button,
   * NOT from the thumbnail's own <img>.
   *
   * Reading the thumbnail's <img> was the bug behind "images 2, 3, 4 are small":
   * the thumbnail is rendered at `sizes="90px"`, so copying its `sizes` and its
   * `width`/`height` onto the main image told the browser the large frame was 90px
   * wide. The browser then picked the ~90px candidate from the srcset and
   * stretched it across a half-page box. The thumbnail now points at the media
   * item's full-size candidate list instead of describing it.
   */
  const showMedia = (mediaId) => {
    const button = qs(`[data-si-media-select="${mediaId}"]`, gallery);
    if (!button) return;

    const {
      siMediaSrc: nextSrc,
      siMediaSrcset: nextSrcset,
      siMediaSizes: nextSizes,
      siMediaWidth: nextWidth,
      siMediaHeight: nextHeight,
      siMediaAlt: nextAlt
    } = button.dataset;

    if (nextSrc) mainImage.src = nextSrc;
    if (nextSrcset) mainImage.srcset = nextSrcset;
    if (nextSizes) mainImage.sizes = nextSizes;

    // Alt text describes the image, so it has to change with it. A stale alt is
    // worse than no alt for anyone using a screen reader.
    if (nextAlt) mainImage.alt = nextAlt;

    // Keep the intrinsic ratio in sync so the reserved space stays correct and
    // the page does not jump when a differently shaped image loads.
    if (nextWidth && nextHeight) {
      mainImage.width = nextWidth;
      mainImage.height = nextHeight;
    }

    qsa('[data-si-media-id]', gallery).forEach((item) => {
      item.classList.toggle('is-active', item.dataset.siMediaId === String(mediaId));
    });

    qsa('[data-si-media-select]', gallery).forEach((btn) => {
      const active = btn.dataset.siMediaSelect === String(mediaId);
      btn.setAttribute('aria-current', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
    });

    document.dispatchEvent(new CustomEvent('si:media:change', { detail: { mediaId } }));
  };

  /* ---------- Click ---------- */

  gallery.addEventListener('click', (event) => {
    const button = event.target.closest('[data-si-media-select]');
    if (!button) return;

    event.preventDefault();
    showMedia(button.dataset.siMediaSelect);
  });

  /* ---------- Keyboard ---------- */

  /**
   * A row of buttons is a keyboard interface. Arrow keys move between
   * thumbnails and Home/End jump to the ends, which is what a keyboard user
   * expects from a gallery. Roving tabindex keeps Tab moving past the whole row
   * rather than through every thumbnail.
   */
  gallery.addEventListener('keydown', (event) => {
    const button = event.target.closest('[data-si-media-select]');
    if (!button) return;

    const index = thumbs.indexOf(button);
    if (index === -1) return;

    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = thumbs[(index + 1) % thumbs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = thumbs[(index - 1 + thumbs.length) % thumbs.length];
        break;
      case 'Home':
        next = thumbs[0];
        break;
      case 'End':
        next = thumbs[thumbs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    next.focus();
    showMedia(next.dataset.siMediaSelect);
  });

  /* ---------- Variant driven ---------- */

  // Selecting a colour should show that colour's photo. product-variant.js emits
  // the event; the gallery only reacts if the variant names a media item.
  document.addEventListener('si:variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant || !variant.featured_media_id) return;

    // Only switch when the media item is actually part of this gallery.
    if (qs(`[data-si-media-select="${variant.featured_media_id}"]`, gallery)) {
      showMedia(variant.featured_media_id);
    }
  });

  /* ---------- Initial state ---------- */

  // The server already marked the first thumbnail active, but the tabindex state
  // is JS-owned, so it is applied once on load.
  const initial = qs('.si-product__thumb.is-active [data-si-media-select]', gallery) || thumbs[0];
  if (initial) {
    qsa('[data-si-media-select]', gallery).forEach((btn) => {
      btn.tabIndex = btn === initial ? 0 : -1;
    });
  }
})();
